/**
 * Structured logging — thin layer over `Sentry.logger`.
 *
 * Sentry is configured in `@root/app/bootstrap/sentry` (`enableLogs: true`). Import `log`
 * from here rather than reaching for `@sentry/react` directly, so every call site picks up
 * the same conventions.
 *
 * ## Conventions
 *
 * 1. **Wide events over fragmented ones.** One log per meaningful outcome, carrying every
 *    attribute you would want to filter or group by — not five logs describing one flow.
 * 2. **Logs are not exceptions.** `log.*` is searchable observability data; use
 *    `Sentry.captureException` (via `errorService.reportError`) when you need issue
 *    grouping, alerting and a stack trace. An unexpected throw deserves both.
 * 3. **`log.fmt` for sentences with variables**, plain attributes for named facts.
 *    `fmt` is a tagged template — `` log.info(log.fmt`Order ${id} shipped`) `` — and turns
 *    each interpolated value into an independently searchable `message.parameter.N`.
 * 4. **No secrets, no PII.** `beforeSendLog` scrubs obvious keys as a backstop, but the
 *    rule is to leave them out here. Ids over emails, `login_method` over credentials.
 * 5. **Levels:** `info` for business milestones, `warn` for degraded-but-recovered,
 *    `error` for failures that need investigation, `fatal` for unrecoverable state.
 *    `debug`/`trace` are dropped in production — do not rely on them there.
 *
 * ## Example
 *
 * ```ts
 * import { log, logAttributes } from '@root/app/shared/new-services/logging.service';
 *
 * log.info('Survey published', logAttributes({
 *     survey_id: survey.id,
 *     question_count: survey.questions.length,
 *     audience_size: audience.total,
 *     scheduled: Boolean(survey.startDate),
 *     duration_ms: Date.now() - startedAt
 * }));
 * ```
 */
import * as Sentry from '@sentry/react';

/** The only attribute value types Sentry accepts. */
export type LogAttributeValue = string | number | boolean;

export type LogAttributes = Record<string, LogAttributeValue>;

/**
 * Normalise a loosely-typed bag into something Sentry will actually store.
 *
 * Sentry silently drops attributes whose value is not a string, number or boolean, which
 * makes a typo look identical to a missing field in the log explorer. Most of our call
 * sites build attributes out of optional DTO fields, so run them through here:
 *
 * - `undefined` / `null` entries are omitted (an absent attribute, not a `"undefined"` string)
 * - arrays are joined into a comma-separated string, so they stay filterable
 * - anything else non-primitive is JSON-stringified rather than dropped
 */
export const logAttributes = (input: Record<string, unknown>): LogAttributes => {
    const attributes: LogAttributes = {};

    Object.entries(input).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            return;
        }

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            attributes[key] = value;
            return;
        }

        if (Array.isArray(value)) {
            attributes[key] = value.join(',');
            return;
        }

        attributes[key] = JSON.stringify(value);
    });

    return attributes;
};

/**
 * `log.trace` / `debug` / `info` / `warn` / `error` / `fatal`, plus the `log.fmt`
 * tagged template for parameterised messages.
 */
export const log = Sentry.logger;
